/**
 * Helper reutilizable de opt-in de Web Push (front).
 *
 * Lo usan los fronts (promoter, customer, cashier web) para activar notificaciones:
 *   1. registra el service worker (si no estaba),
 *   2. pide permiso de notificaciones (diálogo nativo),
 *   3. suscribe al pushManager con la VAPID pública,
 *   4. manda la subscription al backend (vía la función `postSubscription` que pasa
 *      el caller, con su propia auth).
 *
 * SIN dependencias externas. Funciona donde el navegador soporta Push (Android/
 * desktop siempre; iOS 16.4+ SOLO si la PWA está instalada en el inicio).
 *
 * El caller controla auth y rutas (cada front tiene las suyas) pasando:
 *   - swUrl: ruta del service worker (ej. '/sw.js').
 *   - registrationUrl: URL cuyo scope debe controlar el SW (opcional; por defecto swUrl).
 *   - getVapidPublicKey: async () => string  (pega al endpoint del backend).
 *   - postSubscription: async (subJson) => {ok}  (manda la sub con su auth).
 */

/** ¿El navegador soporta Web Push? */
export function pushSoportado() {
    return (
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        typeof window !== 'undefined' &&
        'PushManager' in window &&
        'Notification' in window
    );
}

/** Estado actual del permiso: 'default' | 'granted' | 'denied' | 'unsupported'. */
export function estadoPermiso() {
    if (!pushSoportado()) return 'unsupported';
    return Notification.permission;
}

/** ¿La PWA está corriendo instalada (standalone)? Relevante para iOS (push requiere instalada). */
export function esStandalone() {
    return (
        (typeof window !== 'undefined' &&
            window.matchMedia &&
            window.matchMedia('(display-mode: standalone)').matches) ||
        // iOS Safari legacy
        (typeof navigator !== 'undefined' && navigator.standalone === true)
    );
}

/** ¿Es iOS (iPhone/iPad)? Para guiar el flujo "agregar al inicio" obligatorio. */
export function esIOS() {
    if (typeof navigator === 'undefined') return false;
    return (
        /iphone|ipad|ipod/i.test(navigator.userAgent) ||
        // iPad moderno se reporta como Mac con touch
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
}

/**
 * Activa el push: registra SW + pide permiso + suscribe + manda la sub al backend.
 * NO lanza — devuelve un resultado describiendo qué pasó, para que la UI decida.
 * @param {{ swUrl:string, registrationUrl?:string, getVapidPublicKey:()=>Promise<string>, postSubscription:(sub:object)=>Promise<any> }} opts
 * @returns {Promise<{ ok:boolean, estado:string, motivo?:string }>}
 *   estado: 'activado' | 'denegado' | 'no-soportado' | 'ios-requiere-instalar' | 'error'
 */
export async function activarPush({ swUrl, registrationUrl = swUrl, getVapidPublicKey, postSubscription }) {
    if (!pushSoportado()) return { ok: false, estado: 'no-soportado' };

    // iOS: el push SOLO funciona si la PWA está instalada en el inicio. Si es iOS
    // y NO está standalone, avisamos para que el usuario la agregue primero.
    if (esIOS() && !esStandalone()) {
        return { ok: false, estado: 'ios-requiere-instalar' };
    }

    try {
        // 1. Registrar el service worker (idempotente: si ya está, devuelve el existente).
        const reg =
            (await navigator.serviceWorker.getRegistration(registrationUrl)) ||
            (await navigator.serviceWorker.register(swUrl));
        await navigator.serviceWorker.ready;

        // 2. Pedir permiso (diálogo nativo). Solo se dispara por gesto del usuario.
        const permiso = await Notification.requestPermission();
        if (permiso !== 'granted') {
            return { ok: false, estado: 'denegado' };
        }

        // 3. Conseguir la VAPID pública del backend y suscribir.
        const vapidPublic = await getVapidPublicKey();
        if (!vapidPublic) return { ok: false, estado: 'error', motivo: 'sin-vapid' };

        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: _base64UrlToUint8Array(vapidPublic),
            });
        }

        // 4. Mandar la subscription al backend (el caller pone su auth).
        const res = await postSubscription(sub.toJSON());
        if (!res || res.ok === false) return { ok: false, estado: 'error', motivo: 'backend' };

        return { ok: true, estado: 'activado' };
    } catch (err) {
        return { ok: false, estado: 'error', motivo: (err && err.message) || 'desconocido' };
    }
}

/** Convierte la VAPID pública (base64url) al Uint8Array que espera pushManager.subscribe. */
function _base64UrlToUint8Array(base64Url) {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}
